import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  categoryColor,
  CLASS_COLORS,
  classStacksOverTime,
  fetchFleetCompositionBundle,
  fuelLabel,
  FUEL_COLORS,
  fuelStacksOverTime,
  type FleetCompositionBundle,
  type FleetCompositionMetrics,
  vehicleCategoryLabel,
} from '../../services/fleetCompositionService';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import {
  ChartLayout,
  EquityContent,
  MethodologyContent,
  OverviewContent,
  RelevanceContent,
} from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import './ChartLayout.css';

Chart.register(...registerables);

export type FleetCompositionChartView = 'intensity' | 'by_class' | 'by_fuel';

interface Props {
  location: string;
  geofips: number | null;
  chartView?: FleetCompositionChartView;
  onChartViewChange?: (view: FleetCompositionChartView) => void;
  onMetricsCalculated?: (m: FleetCompositionMetrics | null) => void;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtOne(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
}

function signedPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

export const FleetCompositionSection: React.FC<Props> = ({
  location,
  geofips,
  chartView: controlledView,
  onChartViewChange,
  onMetricsCalculated,
}) => {
  const [internalView, setInternalView] = useState<FleetCompositionChartView>('by_fuel');
  const chartView = controlledView ?? internalView;
  const setChartView = (v: FleetCompositionChartView) => {
    onChartViewChange?.(v);
    if (controlledView == null) setInternalView(v);
  };

  const [activePanel, setActivePanel] = useState('overview');
  const [bundle, setBundle] = useState<FleetCompositionBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [classMode, setClassMode] = useState<'vehicle_category' | 'class'>('vehicle_category');
  const [fuelScope, setFuelScope] = useState<'all' | 'LDV'>('LDV');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const metricsCbRef = useRef(onMetricsCalculated);
  useEffect(() => {
    metricsCbRef.current = onMetricsCalculated;
  }, [onMetricsCalculated]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchFleetCompositionBundle(location, geofips);
        if (cancelled) return;
        setBundle(data);
        metricsCbRef.current?.(data?.metrics ?? null);
      } catch (e: unknown) {
        if (!cancelled) {
          setBundle(null);
          setError(e instanceof Error ? e.message : 'Failed to load fleet composition');
          metricsCbRef.current?.(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (location && geofips != null) load();
    else {
      setLoading(false);
      setBundle(null);
    }
    return () => {
      cancelled = true;
    };
  }, [location, geofips]);

  const metrics = bundle?.metrics ?? null;

  const overviewText = useMemo(() => {
    if (!metrics) return `Loading vehicle fleet composition for ${location}...`;
    const vsCa =
      metrics.caLdvGPerMileLatest != null
        ? metrics.ldvGPerMileLatest < metrics.caLdvGPerMileLatest
          ? `lower than California's ${fmtOne(metrics.caLdvGPerMileLatest)} gCO₂e/mi`
          : metrics.ldvGPerMileLatest > metrics.caLdvGPerMileLatest
            ? `higher than California's ${fmtOne(metrics.caLdvGPerMileLatest)} gCO₂e/mi`
            : `about the same as California (${fmtOne(metrics.caLdvGPerMileLatest)} gCO₂e/mi)`
        : 'compared with California statewide';
    const trend =
      metrics.ldvGPerMilePctChange < -1
        ? `declined ${fmtOne(Math.abs(metrics.ldvGPerMilePctChange))}% since ${metrics.baselineYear}`
        : metrics.ldvGPerMilePctChange > 1
          ? `increased ${fmtOne(metrics.ldvGPerMilePctChange)}% since ${metrics.baselineYear}`
          : `changed little since ${metrics.baselineYear}`;

    return (
      `In ${metrics.latestYear}, the light-duty fleet in ${location} averaged ${fmtOne(metrics.ldvGPerMileLatest)} gCO₂e per mile — ${vsCa}. ` +
      `LDV carbon intensity has ${trend}. ` +
      `Of about ${fmtInt(metrics.totalVehiclesLatest)} registered vehicles, ${fmtOne(metrics.ldvSharePctLatest)}% are light-duty, ` +
      `and ${fmtOne(metrics.zeroEmissionSharePctLatest)}% of LDVs are battery-electric, plug-in hybrid, or hydrogen.`
    );
  }, [metrics, location]);

  useEffect(() => {
    if (!bundle || !canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartView === 'intensity') {
      const years = Array.from(
        new Set([
          ...bundle.ldvIntensityLocation.map((d) => d.year),
          ...bundle.ldvIntensityCalifornia.map((d) => d.year),
        ])
      ).sort((a, b) => a - b);
      const locMap = new Map(bundle.ldvIntensityLocation.map((d) => [d.year, d.value]));
      const caMap = new Map(bundle.ldvIntensityCalifornia.map((d) => [d.year, d.value]));

      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: years,
          datasets: [
            {
              label: location,
              data: years.map((y) => locMap.get(y) ?? null),
              borderColor: '#2563eb',
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              tension: 0.25,
              spanGaps: true,
              pointRadius: 3,
            },
            {
              label: 'California',
              data: years.map((y) => caMap.get(y) ?? null),
              borderColor: '#64748b',
              backgroundColor: 'rgba(100, 116, 139, 0.08)',
              borderDash: [6, 4],
              tension: 0.25,
              spanGaps: true,
              pointRadius: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: standardLegend,
            title: standardTitle('LDV carbon intensity (gCO₂e/mile)'),
            tooltip: {
              callbacks: {
                label: (item) => {
                  const v = item.parsed.y;
                  if (v == null || !Number.isFinite(v)) return `${item.dataset.label}: —`;
                  return `${item.dataset.label}: ${fmtOne(v)} gCO₂e/mi`;
                },
              },
            },
          },
          scales: {
            y: {
              title: { display: true, text: 'gCO₂e per mile' },
              beginAtZero: true,
            },
            x: { title: { display: true, text: 'Year' } },
          },
        },
      });
      return;
    }

    const stackedYAxis = {
      stacked: true as const,
      beginAtZero: true,
      title: { display: true, text: 'Registered vehicles' },
      ticks: {
        callback: (v: string | number) => {
          const n = Number(v);
          if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
          if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
          return String(v);
        },
      },
    };

    if (chartView === 'by_class') {
      const stacks = classStacksOverTime(bundle.rows, classMode);
      const keys = Array.from(new Set(stacks.flatMap((s) => Object.keys(s.byKey)))).sort((a, b) => {
        if (classMode === 'class') {
          const order = ['LDV', 'HDV'];
          return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
        }
        const order = ['P', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'MC', 'MH', 'BS', 'BT', 'B'];
        return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
      });
      const labelFn =
        classMode === 'class'
          ? (k: string) => (k === 'LDV' ? 'Light-duty (LDV)' : k === 'HDV' ? 'Heavy-duty (HDV)' : k)
          : vehicleCategoryLabel;

      chartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: stacks.map((s) => String(s.year)),
          datasets: keys.map((key, i) => ({
            label: labelFn(key),
            data: stacks.map((s) => s.byKey[key] || 0),
            backgroundColor:
              classMode === 'class' ? CLASS_COLORS[key] || categoryColor(key, i) : categoryColor(key, i),
            stack: 'type',
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: standardLegend,
            title: standardTitle(
              classMode === 'class'
                ? 'Registered fleet by duty class'
                : 'Registered fleet by vehicle type'
            ),
            tooltip: {
              callbacks: {
                label: (item) => `${item.dataset.label}: ${fmtInt(Number(item.parsed.y) || 0)}`,
              },
            },
          },
          scales: {
            x: { stacked: true, title: { display: true, text: 'Year' } },
            y: stackedYAxis,
          },
        },
      });
      return;
    }

    // by_fuel stacked bar
    const stacks = fuelStacksOverTime(bundle.rows, {
      classFilter: fuelScope === 'LDV' ? 'LDV' : null,
    });
    const fuelKeys = Array.from(
      new Set(stacks.flatMap((s) => Object.keys(s.byFuel)))
    ).sort((a, b) => {
      const order = ['Gasoline', 'Diesel', 'PluginHybrid', 'Electric', 'Natural Gas', 'Hydrogen'];
      return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
    });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: stacks.map((s) => String(s.year)),
        datasets: fuelKeys.map((fuel) => ({
          label: fuelLabel(fuel),
          data: stacks.map((s) => s.byFuel[fuel] || 0),
          backgroundColor: FUEL_COLORS[fuel] || '#94a3b8',
          stack: 'fuel',
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(
            fuelScope === 'LDV'
              ? 'Light-duty fleet by fuel type'
              : 'Registered fleet by fuel type'
          ),
          tooltip: {
            callbacks: {
              label: (item) => `${item.dataset.label}: ${fmtInt(Number(item.parsed.y) || 0)}`,
            },
          },
        },
        scales: {
          x: { stacked: true, title: { display: true, text: 'Year' } },
          y: stackedYAxis,
        },
      },
    });
  }, [bundle, chartView, classMode, fuelScope, location]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  const downloadCsv = () => {
    if (!bundle) return;
    if (chartView === 'intensity') {
      const caMap = new Map(bundle.ldvIntensityCalifornia.map((d) => [d.year, d.value]));
      const rows = bundle.ldvIntensityLocation.map((d) => ({
        year: d.year,
        location_gco2e_per_mile: Number(d.value.toFixed(2)),
        california_gco2e_per_mile: caMap.has(d.year) ? Number(caMap.get(d.year)!.toFixed(2)) : '',
      }));
      downloadDataAsCSV(rows, formatFilename(location, 'Fleet_LDV_Intensity'));
      return;
    }
    if (chartView === 'by_class') {
      const stacks = classStacksOverTime(bundle.rows, classMode);
      const keys = Array.from(new Set(stacks.flatMap((s) => Object.keys(s.byKey))));
      downloadDataAsCSV(
        stacks.map((s) => {
          const row: Record<string, number | string> = { year: s.year, total: Math.round(s.total) };
          keys.forEach((k) => {
            row[k] = Math.round(s.byKey[k] || 0);
          });
          return row;
        }),
        formatFilename(location, `Fleet_Mix_By_Type_${classMode}`)
      );
      return;
    }
    const stacks = fuelStacksOverTime(bundle.rows, {
      classFilter: fuelScope === 'LDV' ? 'LDV' : null,
    });
    const fuels = Array.from(new Set(stacks.flatMap((s) => Object.keys(s.byFuel))));
    downloadDataAsCSV(
      stacks.map((s) => {
        const row: Record<string, number | string> = { year: s.year, total: Math.round(s.total) };
        fuels.forEach((f) => {
          row[f] = Math.round(s.byFuel[f] || 0);
        });
        return row;
      }),
      formatFilename(location, `Fleet_By_Fuel_${fuelScope}`)
    );
  };

  const downloadPng = () => {
    const canvas = document.querySelector('#fleet-composition-chart canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = formatFilename(location, `Fleet_Composition_${chartView}`) + '.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <ChartLayout
      title="Vehicle Fleet Composition"
      description="Registered vehicle mix and light-duty carbon intensity from the CARB EMFAC Fleet Database (DMV registrations)."
      chartType={chartView === 'intensity' ? 'line' : 'bar'}
      chartSelector={{
        options: [
          { value: 'by_fuel', label: 'Mix by fuel' },
          { value: 'by_class', label: 'Mix by type' },
          { value: 'intensity', label: 'LDV gCO₂e/mile' },
        ],
        value: chartView,
        onChange: (v) => setChartView(v as FleetCompositionChartView),
      }}
      additionalButtonGroups={
        chartView === 'by_class'
          ? [
              {
                label: 'Group',
                options: [
                  { value: 'vehicle_category', label: 'Category' },
                  { value: 'class', label: 'LDV / HDV' },
                ],
                value: classMode,
                onChange: (v) => setClassMode(v as 'vehicle_category' | 'class'),
              },
            ]
          : chartView === 'by_fuel'
            ? [
                {
                  label: 'Scope',
                  options: [
                    { value: 'LDV', label: 'Light-duty' },
                    { value: 'all', label: 'All vehicles' },
                  ],
                  value: fuelScope,
                  onChange: (v) => setFuelScope(v as 'all' | 'LDV'),
                },
              ]
            : undefined
      }
      keyMetrics={
        metrics ? (
          <KeyMetrics>
            <KeyMetric
              value={fmtOne(metrics.ldvGPerMileLatest)}
              label={`LDV gCO₂e/mi (${metrics.latestYear})`}
              color="#1e3a8a"
            />
            <KeyMetric
              value={signedPct(metrics.ldvGPerMilePctChange)}
              label={`Change since ${metrics.baselineYear}`}
              color={metrics.ldvGPerMilePctChange <= 0 ? '#10b981' : '#dc2626'}
            />
            <KeyMetric
              value={`${fmtOne(metrics.zeroEmissionSharePctLatest)}%`}
              label="ZEV share of LDV"
              color="#059669"
            />
          </KeyMetrics>
        ) : undefined
      }
      chartContent={
        <div id="fleet-composition-chart" style={{ width: '100%' }}>
          {loading && <div style={{ padding: 24, color: '#64748b' }}>Loading fleet data…</div>}
          {error && <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>}
          {!loading && !error && !bundle && (
            <div style={{ padding: 24, color: '#64748b' }}>
              No CARB Fleet Database rows found for {location}.
            </div>
          )}
          {!loading && !error && bundle && (
            <div style={{ position: 'relative', height: 420, width: '100%' }}>
              <canvas ref={canvasRef} />
            </div>
          )}
        </div>
      }
      overviewContent={
        <OverviewContent
          title="Fleet Composition Overview"
          description="How the local registered vehicle fleet is composed and how clean light-duty miles are becoming."
        >
          <p>{overviewText}</p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action"
          description="Fleet composition and LDV carbon intensity connect registration trends to on-road greenhouse gas emissions."
        >
          <div className="methodology-details-simple">
            <h5>Emissions intensity of driving</h5>
            <p>
              Grams CO₂e per mile for light-duty vehicles is the fleet-average factor used with VMT to estimate
              passenger-vehicle emissions. Tracking it over time shows whether electrification and cleaner ICE
              technology are delivering lower emissions per mile.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Technology &amp; fuel mix</h5>
            <p>
              Shifts from gasoline toward battery-electric and plug-in hybrid vehicles are the primary local levers
              for cutting transportation emissions when VMT remains high. Category and fuel charts show how far that
              transition has progressed.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Heavy-duty &amp; freight</h5>
            <p>
              Heavy-duty trucks and buses are a smaller share of registrations but a large share of diesel use and
              local air pollution. Separating LDV and HDV helps prioritize ZEVs where they matter most for climate
              and community health.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="Fleet averages hide who owns which vehicles and who breathes the pollution from remaining diesel and high-emitting fleets."
        >
          <div className="methodology-details-simple">
            <h5>Access to clean vehicles</h5>
            <p>
              Zero-emission shares rise first in higher-income areas with home charging. Used-EV incentives, financing,
              and multifamily charging are needed so lower-income drivers can join the transition.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Near-roadway communities</h5>
            <p>
              Neighborhoods near freeways and freight corridors bear disproportionate diesel PM and NOx. HDV
              electrification and cleaner freight routes matter as much as passenger EV adoption for health equity.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Registration vs. travel</h5>
            <p>
              Fleet DB counts vehicles registered to addresses in the place. Commuters and through-traffic may differ
              from the resident fleet — pair these charts with VMT and mode-share metrics for a fuller equity picture.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Methodology"
          description="CARB EMFAC Fleet Database rollups used for local fleet composition and LDV intensity."
          dataSources={[
            'CARB EMFAC Fleet Database (DMV vehicle registrations) via emfac2025_fleet_mat',
            'EMFAC county model factors joined onto FleetDB strata for gCO₂e/mile',
            'California statewide comparison: geography_type = State, geofips = 6',
          ]}
          methodology={[
            `Match ${location} to city or county geofips and query emfac2025_fleet_mat (fallback: emfac2021_fleet_mat).`,
            'LDV gCO₂e/mile is the VMT-weighted average of stratum gco2_per_mile for class = LDV (total_vmt, or vmt_per_veh × population when needed); the y-axis starts at zero.',
            'Mix-by-fuel and mix-by-type are stacked bars of vehicle_population over years (fuel_type, or EMFAC vehicle_category / LDV–HDV).',
            'Zero-emission share = (Electric + PluginHybrid + Hydrogen) ÷ LDV population in the latest year.',
          ]}
        />
      }
      activeView={activePanel}
      onViewChange={setActivePanel}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
      overviewSectionId="transportation"
      overviewChartId="fleet_composition"
      onDownloadPNG={downloadPng}
      onDownloadCSV={downloadCsv}
      pngFilename={formatFilename(location, `Fleet_Composition_${chartView}`)}
      csvFilename={formatFilename(location, `Fleet_Composition_${chartView}_Data`)}
      registerPng={false}
    />
  );
};

export default FleetCompositionSection;
